'use client';

import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRef, useState, type ReactNode } from 'react';

import { Button } from '@flipster/react/design-system/button';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { getQuoteCurrencyPrecision, type QuoteCurrencyList } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';
import type { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { rawInstrumentsKeyBySymbolAtom } from '../../snapshot/state';
import { SymbolScope } from '../../symbol/bunja';
import { tradingSessionCheckBunja } from '../../trading-suspension/state';
import { AdjustOrderModal } from './modal';

const sizeInputModes = ['quantity', 'usdt', 'margin'] as const;
export type SizeInputMode = (typeof sizeInputModes)[number];

const arrowHeight = 7;
const arrowWidth = 16;
const DropdownSelect = createDropdownSelect<SizeInputMode>();

interface AdjustOrderPopoverProps {
  children: ReactNode;
  symbol: string;
  side: V2OrderSideType;
  initLeverage: number;
  marginType: OrderMarginType;
  isLimit: boolean;
  currency: string;
  quoteCurrency: (typeof QuoteCurrencyList)[number];
  pricePrecision: number;
  qtyPrecision: number;
  priceLabel: string;
  priceValue: string;
  qtyValue: string;
  /**
   * 팝오버/모달을 열기 직전에 실행하는 게이트. `false`를 반환하면 열리지 않는다.
   * (off-hours amend 알림처럼 "열기 전" notice를 띄우는 용도 — checkTradable 다음에 평가된다.)
   */
  onBeforeOpen?: () => boolean | Promise<boolean>;
  // Return a Promise to keep the popover/modal open on rejection; resolve to close.
  onConfirm: (
    price: string | undefined,
    sizeValue: string | undefined,
    sizeMode: SizeInputMode,
  ) => void | Promise<void>;
}

export const AdjustOrderPopover = ({
  children,
  symbol,
  side,
  initLeverage,
  marginType,
  isLimit,
  currency,
  quoteCurrency,
  pricePrecision,
  qtyPrecision,
  priceLabel,
  priceValue,
  qtyValue,
  onBeforeOpen,
  onConfirm,
}: AdjustOrderPopoverProps) => {
  const t = useTranslations();
  const { isMobile } = useBreakpoint();
  const { checkTradable } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);

  const [isOpen, setIsOpen] = useState(false);
  const [price, setPrice] = useState(priceValue);
  const [qty, setQty] = useState(qtyValue);
  const [sizeMode, setSizeMode] = useState<SizeInputMode>('quantity');
  const [priceDirty, setPriceDirty] = useState(false);
  const [qtyDirty, setQtyDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !isMobile,
    onOpenChange: (next) => {
      // Block outside-click / escape dismiss while a mutation is in flight.
      if (!next && isSubmitting) return;
      setIsOpen(next);
    },
    placement: 'bottom-start',
    middleware: [
      offset(arrowHeight + 4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef, padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const instruments = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const instrument = instruments?.[symbol];
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);

  const computePrefilledQty = (mode: SizeInputMode) => {
    const q = Num(qtyValue || '0');
    const p = Num(priceValue || '0');
    if (mode === 'quantity') return q.toFixed(qtyPrecision);
    if (p.isZero()) return '0';
    if (mode === 'usdt') return q.multipliedBy(p).toFixed(quoteCurrencyPrecision);
    return initLeverage > 0 ? q.multipliedBy(p).dividedBy(initLeverage).toFixed(quoteCurrencyPrecision) : '0';
  };

  const handleOpen = async () => {
    if (!checkTradable()) return;
    // 열기 전 게이트(off-hours amend 알림 등). false면 열지 않는다.
    if (onBeforeOpen && !(await onBeforeOpen())) return;
    setPrice(priceValue);
    setQty(computePrefilledQty(sizeMode));
    setPriceDirty(false);
    setQtyDirty(false);
    setIsOpen(true);
  };

  const handleConfirm = async () => {
    const nextPrice = priceDirty ? price : undefined;
    const nextQty = qtyDirty ? qty : undefined;
    try {
      setIsSubmitting(true);
      await onConfirm(nextPrice, nextQty, sizeMode);
      setIsOpen(false);
    } catch {
      // Keep the popover/modal open on backend error; the mutation hook surfaces the toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setIsOpen(false);
  };

  const sizeLabel = {
    quantity: t('Position_add_position.r250401.Input_position_quantity_title'),
    usdt: t('Position_add_position.r250401.Input_position_usdt_title'),
    margin: t('Position_add_position.r250401.Input_margin_title'),
  }[sizeMode];

  const handleSizeModeChange = (nextMode: SizeInputMode) => {
    if (nextMode === sizeMode) return;
    const p = Num(price || '0');
    const entered = Num(qty || '0');
    if (!p.isZero() && !entered.isZero()) {
      // Normalize current value to base quantity, then convert to the target mode.
      const asQuantity =
        sizeMode === 'quantity'
          ? entered
          : sizeMode === 'usdt'
            ? entered.dividedBy(p)
            : initLeverage > 0
              ? entered.multipliedBy(initLeverage).dividedBy(p)
              : Num(0);
      const converted =
        nextMode === 'quantity'
          ? asQuantity
          : nextMode === 'usdt'
            ? asQuantity.multipliedBy(p)
            : initLeverage > 0
              ? asQuantity.multipliedBy(p).dividedBy(initLeverage)
              : Num(0);
      const nextPrecision = nextMode === 'quantity' ? qtyPrecision : quoteCurrencyPrecision;
      setQty(converted.toFixed(nextPrecision));
    }
    setSizeMode(nextMode);
  };
  const minQty = instrument ? Num(instrument.unitOrderQty) : Num(0);
  const minAmount = instrument ? Num(instrument.notionalMinOrderAmount) : Num(0);
  const minMargin = initLeverage > 0 ? minAmount.dividedBy(initLeverage) : Num(0);

  const sizeError = (() => {
    const entered = Num(qty || '0');
    if (entered.isZero()) return null;
    if (sizeMode === 'quantity' && !minQty.isZero() && entered.isLessThan(minQty)) {
      return t('Trade.r250401.Min_quantity_required_error', {
        symbol: currency,
        amount: numberFormatter(minQty, { precision: qtyPrecision, toFixed: true }),
      });
    }
    if (sizeMode === 'usdt' && !minAmount.isZero() && entered.isLessThan(minAmount)) {
      return t('Trade.r250401.Min_position_size_required_error', {
        amount: numberFormatter(minAmount, { precision: quoteCurrencyPrecision, toFixed: true }),
      });
    }
    if (sizeMode === 'margin' && !minMargin.isZero() && entered.isLessThan(minMargin)) {
      return t('Trade.r250401.Min_margin_required_error', {
        amount: numberFormatter(minMargin, { precision: quoteCurrencyPrecision, toFixed: true }),
      });
    }
    return null;
  })();

  const isUnchanged = !priceDirty && !qtyDirty;

  return (
    <>
      <span ref={refs.setReference} onClick={handleOpen} style={{ cursor: 'pointer' }}>
        {children}
      </span>

      {/* Mobile: full modal */}
      {isMobile && (
        <AdjustOrderModal
          isOpen={isOpen}
          onClose={handleCancel}
          symbol={symbol}
          side={side}
          initLeverage={initLeverage}
          marginType={marginType}
          isLimit={isLimit}
          currency={currency}
          quoteCurrency={quoteCurrency}
          pricePrecision={pricePrecision}
          qtyPrecision={qtyPrecision}
          priceLabel={priceLabel}
          priceValue={priceValue}
          qtyValue={qtyValue}
          onConfirm={onConfirm}
        />
      )}

      {/* Desktop: inline popover */}
      {!isMobile && isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={css({
              zIndex: 'modal',
              bg: 'background.default.level-1',
              rounded: 12,
              w: 237,
              boxShadow: '0 8px 40px 0 rgba(0, 0, 0, 0.8)',
            })}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={arrowWidth}
              height={arrowHeight}
              fill='var(--colors-background-default-level-2)'
            />
            <div className={vstack({ gap: 16, px: 16, pt: 16, pb: 0, alignItems: 'stretch' })}>
              <div className={vstack({ gap: 6, alignItems: 'stretch' })}>
                <label className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
                  {priceLabel}
                </label>
                <FormattedNumberInput
                  value={price}
                  precision={pricePrecision}
                  onChange={(v) => {
                    setPrice(v);
                    setPriceDirty(true);
                  }}
                  className={css({
                    h: 32,
                    w: 'full',
                    px: 10,
                    rounded: 8,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'border.default.level-2',
                    bg: 'transparent',
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-5',
                    outline: 'none',
                    _focus: { borderColor: 'border.default.level-3' },
                  })}
                />
              </div>
              <div className={vstack({ gap: 6, alignItems: 'stretch' })}>
                <DropdownSelect.Root
                  selected={sizeMode}
                  onChange={handleSizeModeChange}
                  placement='bottom-start'
                  inheritWidth={false}
                >
                  <DropdownSelect.Trigger
                    css={hstack.raw({
                      gap: 4,
                      cursor: 'pointer',
                      textStyle: 'Text/S/Regular',
                      color: 'content.default.level-3',
                    })}
                  >
                    {sizeLabel}
                    <ChevronDownIcon className={css({ w: 14, h: 14, color: 'content.default.level-3' })} />
                  </DropdownSelect.Trigger>
                  <DropdownSelect.Select css={{ w: 205, zIndex: 1 }}>
                    <DropdownSelect.Item
                      value='quantity'
                      label={t('Position_add_position.r250401.Input_position_quantity_title')}
                      subLabel={t('Position_add_position.r250401.Input_position_quantity_description')}
                    />
                    <DropdownSelect.Item
                      value='usdt'
                      label={t('Position_add_position.r250401.Input_position_usdt_title')}
                      subLabel={t('Position_add_position.r250401.Input_position_usdt_description')}
                    />
                    <DropdownSelect.Item
                      value='margin'
                      label={t('Position_add_position.r250401.Input_margin_title')}
                      subLabel={t('Position_add_position.r250401.Input_margin_description')}
                    />
                  </DropdownSelect.Select>
                </DropdownSelect.Root>
                <FormattedNumberInput
                  value={qty}
                  precision={sizeMode === 'quantity' ? qtyPrecision : quoteCurrencyPrecision}
                  onChange={(v) => {
                    setQty(v);
                    setQtyDirty(true);
                  }}
                  className={css({
                    h: 32,
                    w: 'full',
                    px: 10,
                    rounded: 8,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: sizeError ? 'content.state.negative' : 'border.default.level-2',
                    bg: 'transparent',
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-5',
                    outline: 'none',
                    _focus: { borderColor: sizeError ? 'content.state.negative' : 'border.default.level-3' },
                  })}
                />
                {sizeError && (
                  <span
                    className={css({
                      textStyle: 'Text/S/Monospace/Regular',
                      color: 'content.state.negative',
                    })}
                  >
                    {sizeError}
                  </span>
                )}
              </div>
            </div>
            <div className={hstack({ gap: 8, p: 16 })}>
              <Button
                hierarchy='secondary'
                color='neutral'
                size='small'
                label={t('Common.r250401.Cancel')}
                onClick={handleCancel}
                disabled={isSubmitting}
                css={{ flex: 1 }}
              />
              <Button
                hierarchy='primary'
                color='accent'
                size='small'
                label={t('Common.r250401.Confirm')}
                onClick={handleConfirm}
                isLoading={isSubmitting}
                disabled={
                  sizeError !== null || !qty || Num(qty).isZero() || !price || Num(price).isZero() || isUnchanged
                }
                css={{ flex: 1 }}
              />
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
